"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ComposerSkillPicker } from "@/components/composer-skill-picker";
import type { AgentSkill, AgentSkillsStatus } from "@/hooks/use-agent-skills";
import { Check, Download, Play, RefreshCw, Rocket, Upload, Zap } from "lucide-react";
import { DynamicIcon, iconNames, type IconName } from "lucide-react/dynamic";
import type { ComposerAction } from "@/hooks/use-agents";
import { fetchWorkflowStatus } from "@/api/workflows";
import { actionIsRunning, composerRunsForSession, restoreComposerRuns, trackComposerRun, useComposerRunRevision } from "@/lib/composer-action-runs";
import { useWorkflowTrigger } from "@/components/views/workflow/use-workflow-trigger";
import { Button } from "@/components/ui/button";
import { InputGroupButton } from "@/components/ui/input-group";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn, type AccessTokenGetter } from "@/lib/utils";

const actionIcons = { rocket: Rocket, play: Play, "refresh-cw": RefreshCw, upload: Upload, download: Download, check: Check };

function ActionIcon({ name }: { name: string | null }) {
  if (name && !(name in actionIcons) && iconNames.includes(name as IconName)) {
    return <DynamicIcon name={name as IconName} className="size-3.5" aria-hidden="true" />;
  }
  const Icon = name && name in actionIcons ? actionIcons[name as keyof typeof actionIcons] : Zap;
  return <Icon className="size-3.5" aria-hidden="true" />;
}

export interface ComposerQuickActionsProps {
  actions: ComposerAction[];
  agentId: string;
  sessionId: string;
  getToken: AccessTokenGetter;
  hasDraft: boolean;
  narrow: boolean;
  stacked?: boolean;
  disabled: boolean;
  onQueued: (action: ComposerAction, taskId: string) => void;
  transcriptRuns: { taskId: string; actionId: string }[];
  skillsStatus?: AgentSkillsStatus;
  skills?: AgentSkill[];
  selectedSkills?: string[];
  skillsLimitReached?: boolean;
  onToggleSkill?: (name: string) => void;
}

export function ComposerQuickActions({ actions, agentId, sessionId, getToken, hasDraft, narrow, stacked = false, disabled, onQueued, transcriptRuns, skillsStatus = "unavailable", skills = [], selectedSkills = [], skillsLimitReached = false, onToggleSkill }: ComposerQuickActionsProps) {
  const { t } = useTranslation("chat");
  const selectedRef = useRef<ComposerAction | null>(null);
  const confirmationRef = useRef<((approved: boolean) => void) | null>(null);
  useEffect(() => () => { confirmationRef.current?.(false); confirmationRef.current = null; }, []);
  const [confirmAction, setConfirmAction] = useState<ComposerAction | null>(null);
  useComposerRunRevision();
  useEffect(() => {
    restoreComposerRuns(sessionId, (taskId) => fetchWorkflowStatus(taskId, getToken));
    for (const run of transcriptRuns) {
      trackComposerRun(run.taskId, run.actionId, sessionId, () => fetchWorkflowStatus(run.taskId, getToken));
    }
  }, [transcriptRuns, sessionId, getToken]);
  const handleQueued = useCallback((taskId: string) => {
    const action = selectedRef.current;
    if (action) {
      trackComposerRun(taskId, action.id, sessionId, () => fetchWorkflowStatus(taskId, getToken));
      onQueued(action, taskId);
    }
  }, [getToken, onQueued, sessionId]);
  const { isWorkspaceScopedStartSupported, state, triggerFor } = useWorkflowTrigger({
    fileName: actions[0]?.workflow ?? "",
    agentId,
    inputs: {},
    getToken,
    sessionId,
    onQueued: handleQueued,
  });

  const showWorkflowActions = actions.length > 0 && isWorkspaceScopedStartSupported === true;
  const showSkills = skillsStatus === "loading" || (skillsStatus === "ready" && skills.length > 0);
  if (!showWorkflowActions && !showSkills) return null;
  const starting = state.phase === "confirming" || state.phase === "starting";
  const busy = (action: ComposerAction) => disabled || starting || actionIsRunning(sessionId, action.id);
  const statusUnknown = composerRunsForSession(sessionId).some((run) => run.status === "status_unknown");
  const unavailable = composerRunsForSession(sessionId).some((run) => run.status === "unavailable");
  const compact = narrow || hasDraft || stacked;
  const workflowActions = showWorkflowActions ? actions : [];
  const visible = compact ? [] : workflowActions.slice(0, 3);
  const overflow = compact ? workflowActions : workflowActions.slice(3);
  const showZap = showSkills || overflow.length > 0;
  const askConfirmation = (action: ComposerAction) => new Promise<boolean>((resolve) => {
    confirmationRef.current = resolve;
    setConfirmAction(action);
  });
  const resolveConfirmation = (approved: boolean) => {
    confirmationRef.current?.(approved);
    confirmationRef.current = null;
    setConfirmAction(null);
  };
  const start = (action: ComposerAction) => {
    if (busy(action)) return;
    selectedRef.current = action;
    void triggerFor(action.workflow, action.confirm ? () => askConfirmation(action) : undefined, action.id);
  };

  return <>
    <div className={cn("flex shrink-0 items-end gap-1", stacked && "flex-col")} aria-label={t("composerActions.actions")}>
      {visible.map((action) => (
        <InputGroupButton key={action.id} type="button" size="sm" variant="outline" className="max-w-28 gap-1 px-2 text-xs" disabled={busy(action)} onClick={() => start(action)} title={action.label}>
          <ActionIcon name={action.icon} /><span className="truncate">{action.label}</span>
        </InputGroupButton>
      ))}
      {showZap && <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <InputGroupButton type="button" size="icon-sm" variant="outline" disabled={disabled || starting} aria-label={t("composerActions.actions")} title={t("composerActions.actions")}>
            <Zap className="size-4" aria-hidden="true" />
          </InputGroupButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {overflow.map((action) => <DropdownMenuItem key={action.id} disabled={busy(action)} onSelect={() => start(action)}>
            <ActionIcon name={action.icon} />{action.label}
          </DropdownMenuItem>)}
          {showSkills && <ComposerSkillPicker status={skillsStatus} skills={skills} selected={selectedSkills} limitReached={skillsLimitReached} disabled={disabled || starting} onToggle={onToggleSkill ?? (() => {})} />}
        </DropdownMenuContent>
      </DropdownMenu>}
    </div>
    {(statusUnknown || unavailable) && <span role="status" className="max-w-48 text-xs text-muted-foreground">{t(statusUnknown ? "composerActions.status.status_unknown" : "composerActions.status.unavailable")}</span>}
    {state.phase === "error" && <span role="alert" className="max-w-48 text-xs text-destructive">{state.message}</span>}
    <Dialog open={confirmAction !== null} onOpenChange={(open) => { if (!open) resolveConfirmation(false); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("composerActions.confirmTitle", { label: confirmAction?.label })}</DialogTitle>
          <DialogDescription>{t("composerActions.confirmDescription", { label: confirmAction?.label })}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => resolveConfirmation(false)}>{t("common:actions.cancel")}</Button>
          <Button type="button" onClick={() => resolveConfirmation(true)}>{t("composerActions.start")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </>;
}
